import { useState } from "react";
import { motion } from "framer-motion";
import { FileCheck2 } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";

const checklists = {
  "Term Loan": [
    "KYC of the entity, promoters and directors",
    "Audited financials and ITRs for the last 3 years",
    "Bank statements for the last 12 months",
    "Project report with CMA data for the proposed facility",
    "Existing loan sanction letters and repayment track",
    "Collateral documents, if the loan is secured",
  ],
  "Working Capital Demand Loan": [
    "KYC of the entity and promoters",
    "Financials for the last 2–3 years",
    "Bank statements for the last 6–12 months",
    "GST returns for the last 12 months",
    "Latest stock and debtor statements",
    "Existing working capital sanction letters",
  ],
  "Non-Convertible Debenture": [
    "Audited financials for the last 3 years",
    "Memorandum and Articles of Association",
    "Board resolution authorising the issue",
    "Credit rating rationale, if rated",
    "Business plan and use-of-funds statement",
    "Details of existing borrowings and security",
  ],
  "External Commercial Borrowing": [
    "Audited financials for the last 3 years",
    "Board resolution for the borrowing",
    "Draft facility agreement with the foreign lender",
    "FEMA / ECB eligibility documentation",
    "Hedging policy for currency exposure",
    "Existing debt and security details",
  ],
  "Home Loan": [
    "KYC — identity, address and PAN",
    "Income proof — salary slips / ITRs for 2–3 years",
    "Bank statements for the last 6 months",
    "Property papers or agreement to sell",
    "Passport-size photographs",
    "Existing loan details, if any",
  ],
  "Business Loan": [
    "KYC of proprietor/partners/directors",
    "Business proof — GST registration / Udyam",
    "ITRs and financials for the last 2–3 years",
    "Bank statements for the last 12 months",
    "Business vintage proof",
  ],
  "Loan Against Property": [
    "KYC and income proof",
    "Complete property title documents with chain",
    "Latest property tax receipts",
    "Bank statements for the last 6–12 months",
    "Existing loan details on the property, if any",
    "Approved building plan / occupancy certificate",
  ],
  "Gold Loan": [
    "KYC — identity and address proof",
    "Gold ornaments for valuation and pledge",
    "Passport-size photographs",
  ],
  "Purchase Invoice Discounting": [
    "KYC of the firm and promoters",
    "GST registration and recent returns",
    "Bank statements for the last 12 months",
    "Sample purchase invoices and supplier details",
    "Financials for the last 2–3 years",
    "Sales cycle and collection history",
  ],
  "Sales Invoice Discounting": [
    "KYC of the firm and promoters",
    "Sample sales invoices and purchase orders",
    "Buyer list with credit periods and payment history",
    "Debtor ageing and sales ledger",
    "Bank statements for the last 12 months",
    "Financials for the last 2–3 years",
  ],
};

export const ChecklistSection = () => {
  const [type, setType] = useState("Home Loan");

  return (
    <section id="checklists" data-testid="checklists-section" className="py-14 lg:py-20 bg-slate3/40 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <p className="overline-label mb-4">Come prepared</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Documents you'll need.
          </h2>
          <p className="mt-5 text-[#A0AAB2] text-base md:text-lg leading-relaxed">
            A complete file is the single biggest factor in a fast sanction. Pick your loan type
            and see exactly what to keep ready before we approach lenders.
          </p>
          <div className="mt-10 max-w-sm">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="checklist-type-select" className="bg-slate3 border-white/10 rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate2 border-white/10">
                {Object.keys(checklists).map((k) => (
                  <SelectItem key={k} value={k} data-testid={`checklist-type-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        <motion.div
          key={type}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="lg:col-span-7 bg-slate2 border border-white/10 p-8 lg:p-10"
          data-testid="checklist-panel"
        >
          <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-8">
            Checklist — <span className="text-gold">{type}</span>
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            {checklists[type].map((doc) => (
              <li key={doc} className="flex gap-3 text-sm text-foreground/90">
                <FileCheck2 size={16} className="text-gold shrink-0 mt-0.5" /> {doc}
              </li>
            ))}
          </ul>
          <p className="mt-10 text-xs text-muted-foreground leading-relaxed">
            Indicative list — the exact set varies by lender and profile. We'll share a precise
            checklist once we review your enquiry.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
